/**
 * Cascade-policy queries.
 *
 * Routes:
 *   GET    /api/v1/tenants/{id}/cascade-policy
 *   PUT    /api/v1/tenants/{id}/cascade-policy
 *   DELETE /api/v1/tenants/{id}/cascade-policy
 *
 * Writes go through withStepUp because the backend mount is
 * inside the requireStepUp group at the wiring layer — the
 * mutation will surface a 403 step-up-required on a stale
 * session, and withStepUp transparently runs the ceremony.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";

import {
  cascadePolicySchema,
  type CascadePolicy,
  type CascadeMethod,
} from "./schemas";

const base = (tenantID: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/cascade-policy`;

export async function fetchCascadePolicy(
  tenantID: string,
): Promise<CascadePolicy> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return cascadePolicySchema.parse(raw);
}

export function useCascadePolicyQuery(tenantID: string) {
  return useQuery<CascadePolicy, ApiError>({
    queryKey: queryKeys.cascadePolicy.policy(tenantID),
    queryFn: () => fetchCascadePolicy(tenantID),
    // 60s staleTime — the policy changes maybe a handful of
    // times in a tenant's lifetime; no need for hot polling.
    staleTime: 60 * 1000,
  });
}

export function useSetCascadePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<CascadePolicy, ApiError, CascadeMethod[]>({
    mutationFn: (order) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "PUT",
          body: { cascade_order: order },
        }).then((raw) => cascadePolicySchema.parse(raw)),
      ),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.cascadePolicy.policy(tenantID), data);
      qc.invalidateQueries({ queryKey: queryKeys.cascadePolicy.all(tenantID) });
    },
  });
}

export function useClearCascadePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(base(tenantID), {
          method: "DELETE",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cascadePolicy.all(tenantID) });
    },
  });
}
